import { clamp, clamp01, expSmooth, mean, normEntropy, stdev } from '../core/math';
import { Ring } from '../core/ring';
import type { ComboEvent, DefenseEvent, PunchEvent } from '../recog/events';
import type { HitResult } from '../sim/types';

/**
 * SCORING — rated channels, not a point total
 * ===========================================
 *
 * A single accumulating point total has three fatal problems for a skill trainer:
 *
 *   1. It rewards volume. Whoever throws more wins, so the optimal strategy is to
 *      flail, which is the opposite of what we want to teach.
 *   2. It is not comparable across sessions of different lengths.
 *   3. It tells the player nothing. "8,400 points" does not answer "what should I
 *      work on tomorrow?"
 *
 * Instead we maintain ~10 independent CHANNELS, each a 0-100 rating that is an
 * exponentially-weighted estimate of a *rate or ratio*, not a sum. Ratings are
 * length-invariant, directly comparable, and each one maps to a concrete drill.
 * The headline number is a weighted blend, and round outcomes use boxing-style
 * 10-9 judging on the differential rather than on absolute totals — so a
 * defensive round where you landed six clean counters can beat an aggressive
 * round where you landed twenty arm punches, exactly as in real scoring.
 *
 * Every channel also exposes `evidence`, the number of observations behind it, so
 * the UI can grey out a rating nobody has earned yet rather than showing a
 * confident "72" derived from two punches.
 */

export type ChannelId =
  | 'accuracy'
  | 'power'
  | 'combination'
  | 'counter'
  | 'variety'
  | 'rhythm'
  | 'evasion'
  | 'blocking'
  | 'headMovement'
  | 'guardDiscipline'
  | 'reaction'
  | 'punish'
  | 'distance'
  | 'workRate';

export interface Channel {
  id: ChannelId;
  label: string;
  group: 'offence' | 'defence' | 'movement';
  /** 0..100. */
  rating: number;
  /** Observations behind the rating. Low = don't trust the number yet. */
  evidence: number;
  /** What to do about a low score here. Surfaced verbatim in the post-round card. */
  advice: string;
}

const DEF: Array<Omit<Channel, 'rating' | 'evidence'>> = [
  { id: 'accuracy', label: 'Accuracy', group: 'offence', advice: 'Too many punches missing. Close the range before you commit, or shorten the shot.' },
  { id: 'power', label: 'Clean Contact', group: 'offence', advice: 'Your shots are landing glancing. Turn the hip through the punch and finish at full extension.' },
  { id: 'combination', label: 'Combinations', group: 'offence', advice: 'Single shots only. Aim for 2-3 punch strings — the second shot lands because the first moved their guard.' },
  { id: 'counter', label: 'Counters', group: 'offence', advice: 'You are not punishing their recovery. Watch for the hand coming back and fire immediately.' },
  { id: 'variety', label: 'Variety', group: 'offence', advice: 'You are one-note. Mix head and body, straight and hooked — predictability is what the AI reads.' },
  { id: 'rhythm', label: 'Rhythm', group: 'offence', advice: 'Metronomic timing gets read. Break the beat: pause, then fire.' },
  { id: 'evasion', label: 'Head Movement', group: 'defence', advice: 'You are taking clean shots standing still. Slip, roll or pull off the line.' },
  { id: 'blocking', label: 'Blocking', group: 'defence', advice: 'Your guard is not catching shots. Keep the hands high and the elbows in.' },
  { id: 'headMovement', label: 'Activity', group: 'defence', advice: 'You are a static target. Move your head between exchanges, not just when punched at.' },
  { id: 'guardDiscipline', label: 'Guard Discipline', group: 'defence', advice: 'Your hands drop after you punch. Return every shot to the chin.' },
  { id: 'reaction', label: 'Reaction', group: 'defence', advice: 'You are moving late. Watch the shoulder, not the glove — the shoulder moves first.' },
  { id: 'punish', label: 'Punishing', group: 'offence', advice: 'You earn openings and then waste them. The moment their hands drop, let your hands go — two or three shots, not one.' },
  { id: 'distance', label: 'Distance', group: 'movement', advice: 'You are living in their range. Fight at the edge of yours and step out after combinations.' },
  { id: 'workRate', label: 'Work Rate', group: 'movement', advice: 'Not enough output. Sparring is a conversation — you have to talk.' },
];

/** One channel's running estimate. */
class Estimator {
  value = 0.5;
  evidence = 0;
  constructor(
    /** Half-life in *observations*, not ms — channels update per event. */
    private halfLife = 12,
    private prior = 0.5,
  ) {
    this.value = prior;
  }
  observe(x: number, weight = 1): void {
    const k = 1 - Math.pow(0.5, weight / this.halfLife);
    this.value += (clamp01(x) - this.value) * k;
    this.evidence += weight;
  }
  /** Shrink toward the prior while evidence is thin — no confident numbers from noise. */
  get shrunk(): number {
    const t = clamp01(this.evidence / 8);
    return this.prior + (this.value - this.prior) * t;
  }
  reset(): void {
    this.value = this.prior;
    this.evidence = 0;
  }
}

export interface ExchangeRecord {
  t: number;
  playerQuality: number;
  aiQuality: number;
}

export interface RoundResult {
  round: number;
  playerScore: number;
  aiScore: number;
  /** Boxing-style: 10-9, 10-8 for a dominant round, 10-10 only if genuinely even. */
  cards: { player: number; ai: number };
  landed: number;
  thrown: number;
  absorbed: number;
  incoming: number;
  bestCombo: string;
  topAdvice: string[];
}

export class Scorer {
  private est: Record<ChannelId, Estimator>;
  private punchNumbers = new Float32Array(9);
  private targets = { head: 0, body: 0 };
  private intervals = new Ring<number>(40);
  private lastPunchT = 0;

  // Raw round counters, used for the judging card and the summary.
  thrown = 0;
  landed = 0;
  cleanLanded = 0;
  incoming = 0;
  absorbed = 0;
  cleanAbsorbed = 0;
  evaded = 0;
  blocked = 0;
  countersLanded = 0;
  private bestCombo = { notation: '', value: 0 };
  private roundStart = 0;
  private headMoveEvents = 0;
  private exchanges: ExchangeRecord[] = [];
  dodges = 0;
  perfect = 0;
  windowHits = 0;
  windowsWasted = 0;
  readonly reactionSamples: number[] = [];

  constructor() {
    this.est = Object.fromEntries(
      DEF.map((d) => [
        d.id,
        new Estimator(
          d.id === 'variety' || d.id === 'rhythm' ? 20 : 12,
          // Priors reflect what a competent-but-untrained player looks like, so
          // a first-timer starts near the middle rather than at zero.
          d.id === 'accuracy' ? 0.4 : 0.5,
        ),
      ]),
    ) as Record<ChannelId, Estimator>;
  }

  startRound(now: number): void {
    this.roundStart = now;
    this.thrown = 0;
    this.landed = 0;
    this.cleanLanded = 0;
    this.incoming = 0;
    this.absorbed = 0;
    this.cleanAbsorbed = 0;
    this.evaded = 0;
    this.blocked = 0;
    this.countersLanded = 0;
    this.bestCombo = { notation: '', value: 0 };
    this.headMoveEvents = 0;
    this.exchanges.length = 0;
    this.dodges = 0;
    this.perfect = 0;
    this.windowHits = 0;
    this.windowsWasted = 0;
    this.reactionSamples.length = 0;
  }

  // ------------------------------------------------------------- observations

  /**
   * A player punch resolved. `counterWindow` is true if it landed while the AI
   * was recovering — the single highest-value thing a boxer can do, and therefore
   * weighted well above raw accuracy.
   */
  onPlayerPunch(p: PunchEvent, r: HitResult, counterWindow: boolean, now: number): void {
    this.thrown++;
    const w = 0.4 + 0.6 * p.confidence; // low-confidence detections count less

    const landedish = r.outcome === 'clean' || r.outcome === 'glancing';
    if (landedish) this.landed++;
    if (r.outcome === 'clean') this.cleanLanded++;

    // Accuracy counts a blocked punch as a partial success — it was in range and
    // on line, they just handled it. Counting it as a pure miss teaches the wrong
    // lesson (it teaches "don't punch at a good guard").
    const accuracyCredit = r.outcome === 'clean' ? 1 : r.outcome === 'glancing' ? 0.65 : r.outcome === 'blocked' ? 0.4 : 0;
    this.est.accuracy.observe(accuracyCredit, w);

    if (landedish) this.est.power.observe(clamp01(r.quality * 1.15), w);

    if (counterWindow && landedish) {
      this.countersLanded++;
      this.est.counter.observe(1, w * 1.5);
    } else if (r.outcome === 'miss' && p.telegraph > 0.12) {
      // Telegraphed and missed — the worst combination.
      this.est.counter.observe(0.15, w * 0.5);
    }

    // Guard discipline: did the *other* hand stay home, and did this one come back?
    // recoveryMs is patched in when the hand returns, so it may be 0 here; that is
    // handled on the combo event instead.

    this.punchNumbers[p.number] += 1;
    this.targets[p.target] += 1;
    this.est.variety.observe(this.varietyScore(), 1);

    if (this.lastPunchT > 0) {
      const gap = p.tStart - this.lastPunchT;
      if (gap > 60 && gap < 2500) this.intervals.push(gap);
      this.est.rhythm.observe(this.rhythmScore(), 1);
    }
    this.lastPunchT = p.tPeak;

    this.exchanges.push({ t: now, playerQuality: landedish ? r.quality : 0, aiQuality: 0 });
  }

  /** An AI punch resolved against the player. */
  onIncoming(r: HitResult, now: number): void {
    this.incoming++;
    const hit = r.outcome === 'clean' || r.outcome === 'glancing';
    if (hit) this.absorbed++;
    if (r.outcome === 'clean') this.cleanAbsorbed++;
    if (r.outcome === 'slipped') this.evaded++;
    if (r.outcome === 'blocked') this.blocked++;

    // Evasion and blocking are scored separately *and* asymmetrically: slipping is
    // strictly better than blocking (it leaves you in position to counter), so a
    // block is a partial success on the evasion channel, not a failure.
    this.est.evasion.observe(r.outcome === 'slipped' ? 1 : r.outcome === 'miss' ? 0.85 : r.outcome === 'blocked' ? 0.45 : 0);
    this.est.blocking.observe(r.outcome === 'blocked' ? 1 : r.outcome === 'clean' ? 0 : 0.5);

    this.exchanges.push({ t: now, playerQuality: 0, aiQuality: hit ? r.quality : 0 });
  }

  /**
   * A graded dodge. Two separate lessons here, deliberately kept apart:
   *  - `reaction` is how fast you *started* moving. Trainable, and the headline
   *    number the player watches improve week to week.
   *  - `evasion` (fed elsewhere) is whether it worked.
   * Someone can have a fast reaction and a wrong choice, or a slow reaction that
   * still lands because the tell was long. Merging them would hide both.
   */
  onDodge(g: { quality: number; reactionMs: number; perfect: boolean }): void {
    this.dodges++;
    if (g.perfect) this.perfect++;
    // A negative reaction time means the grader could not attribute any movement
    // to this attack (usually a pure block). Counting it would be fabricating a
    // measurement, so it is skipped rather than defaulted.
    if (g.reactionMs >= 0) {
      this.reactionSamples.push(g.reactionMs);
      // 250 ms is about as fast as a human initiates a choice response; 800 ms is
      // "you were not really reacting, the tell was just long enough to survive".
      this.est.reaction.observe(clamp01(1 - (g.reactionMs - 250) / 550), 1.2);
    }
    this.est.evasion.observe(g.quality, 0.6);
  }

  /** A punch landed inside a punish window. `n` is its index in that window. */
  onWindowHit(n: number): void {
    this.windowHits++;
    // Landing anything at all is most of the credit; depth past three is
    // diminishing, so the lesson is "let your hands go", not "flail forever".
    this.est.punish.observe(clamp01(0.45 + 0.28 * Math.min(n, 3)), 1.2);
  }

  /** Called when a window closes unused — the expensive mistake. */
  onWindowWasted(): void {
    this.windowsWasted++;
    this.est.punish.observe(0, 1.5);
  }

  /** Guard-only sampling for punch-out, where there is no distance to score. */
  sampleGuard(dtMs: number, guardMean: number): void {
    this.est.guardDiscipline.observe(guardMean, dtMs / 1000 / 3);
  }

  onDefense(d: DefenseEvent): void {
    this.headMoveEvents++;
    // Deliberate, committed movement scores; twitching does not.
    this.est.headMovement.observe(clamp01(d.depth / 0.4) * clamp01(d.speed / 2.2) * d.confidence, 1);
  }

  onCombo(c: ComboEvent): void {
    const landedCount = c.punches.length;
    // Depth is capped at 4: a 6-punch string is not 50% better than a 4-punch one,
    // and rewarding length without limit produces flailing.
    const depth = clamp01((Math.min(landedCount, 4) - 1) / 3);
    const varietyBonus = c.variety;
    // Tempo band: 130-260 ms between punches is a real combination. Faster is not
    // physically a distinct punch; slower is two separate attacks.
    const tempo = c.tempoMs > 0 ? clamp01(1 - Math.abs(c.tempoMs - 190) / 260) : 0.4;
    const value = depth * (0.55 + 0.25 * varietyBonus + 0.2 * tempo);
    this.est.combination.observe(value, 1.4);

    // Hand-return discipline, now that every punch has recoveryMs filled in.
    const recoveries = c.punches.map((p) => p.recoveryMs).filter((v) => v > 0);
    if (recoveries.length) {
      const avg = mean(recoveries);
      this.est.guardDiscipline.observe(clamp01(1 - (avg - 140) / 320), 1);
    }

    if (value > this.bestCombo.value) this.bestCombo = { notation: c.notation, value };
  }

  /** Called every simulation tick with continuous state. */
  sample(dtMs: number, guardMean: number, gap: number, playerReach: number, aiReach: number): void {
    this.est.guardDiscipline.observe(guardMean, dtMs / 1000 / 3);

    // Distance management scores the *asymmetry*: being where you can hit them and
    // they cannot hit you. Simply "being far away" scores badly, as it should.
    const canHit = gap <= playerReach + 0.05;
    const canBeHit = gap <= aiReach + 0.05;
    const value = canHit && !canBeHit ? 1 : canHit && canBeHit ? 0.55 : !canBeHit ? 0.5 : 0.1;
    this.est.distance.observe(value, dtMs / 1000 / 4);
  }

  tickWorkRate(now: number): void {
    const mins = Math.max(1 / 60, (now - this.roundStart) / 60000);
    const ppm = this.thrown / mins;
    // 35-70 punches/minute is a healthy amateur work rate. Below is passive,
    // far above is almost certainly detection noise or flailing.
    this.est.workRate.observe(clamp01(ppm / 45) * (ppm > 110 ? 0.6 : 1), 0.02);
  }

  // ------------------------------------------------------------------ derived

  private varietyScore(): number {
    const counts = Array.from(this.punchNumbers.slice(1));
    const seq = normEntropy(counts);
    const total = this.targets.head + this.targets.body;
    const bodyMix = total > 0 ? 1 - Math.abs(0.3 - this.targets.body / total) / 0.3 : 0;
    return clamp01(0.7 * seq + 0.3 * clamp01(bodyMix));
  }

  private rhythmScore(): number {
    if (this.intervals.size < 4) return 0.5;
    const xs = this.intervals.toArray();
    const m = mean(xs);
    const cv = m > 0 ? stdev(xs) / m : 0;
    // The ideal is *moderate* variability. Perfectly even timing is a metronome
    // and gets read; wildly uneven timing is not rhythm, it is chaos.
    return clamp01(1 - Math.abs(cv - 0.45) / 0.55);
  }

  channels(): Channel[] {
    return DEF.map((d) => ({
      ...d,
      rating: Math.round(this.est[d.id].shrunk * 100),
      evidence: Math.round(this.est[d.id].evidence),
    }));
  }

  /** The headline number. Weighted so defence and offence matter roughly equally. */
  overall(): number {
    const w: Partial<Record<ChannelId, number>> = {
      accuracy: 1.1,
      power: 0.9,
      combination: 1.0,
      counter: 1.2,
      variety: 0.8,
      rhythm: 0.6,
      evasion: 1.2,
      blocking: 0.8,
      headMovement: 0.7,
      guardDiscipline: 0.9,
      // The two channels this mode exists to train carry the most weight.
      reaction: 1.4,
      punish: 1.3,
      distance: 1.0,
      workRate: 0.6,
    };
    let num = 0;
    let den = 0;
    for (const d of DEF) {
      const k = w[d.id] ?? 1;
      num += this.est[d.id].shrunk * k;
      den += k;
    }
    return Math.round((num / den) * 100);
  }

  static grade(rating: number): string {
    if (rating >= 92) return 'S';
    if (rating >= 84) return 'A';
    if (rating >= 74) return 'B';
    if (rating >= 62) return 'C';
    if (rating >= 48) return 'D';
    return 'E';
  }

  /** The two or three things most worth working on. Ordered by weighted deficit. */
  advice(n = 3): string[] {
    return this.channels()
      .filter((c) => c.evidence >= 4)
      .sort((a, b) => a.rating - b.rating)
      .slice(0, n)
      .map((c) => `${c.label}: ${c.advice}`);
  }

  /**
   * Round card. Modelled on the real ten-point-must system, driven by the
   * differential in *effective* work rather than raw counts.
   */
  finishRound(roundNumber: number): RoundResult {
    const playerWork = this.exchanges.reduce((s, e) => s + e.playerQuality, 0);
    const aiWork = this.exchanges.reduce((s, e) => s + e.aiQuality, 0);
    const total = playerWork + aiWork;
    const share = total > 0 ? playerWork / total : 0.5;

    let cards = { player: 10, ai: 10 };
    if (share > 0.72) cards = { player: 10, ai: 8 };
    else if (share > 0.55) cards = { player: 10, ai: 9 };
    else if (share < 0.28) cards = { player: 8, ai: 10 };
    else if (share < 0.45) cards = { player: 9, ai: 10 };

    return {
      round: roundNumber,
      playerScore: this.overall(),
      aiScore: Math.round((1 - share) * 100),
      cards,
      landed: this.landed,
      thrown: this.thrown,
      absorbed: this.absorbed,
      incoming: this.incoming,
      bestCombo: this.bestCombo.notation,
      topAdvice: this.advice(3),
    };
  }

  /** Mean reaction time this round, ms. 0 when no dodges landed. */
  meanReactionMs(): number {
    return this.reactionSamples.length ? mean(this.reactionSamples) : 0;
  }

  bestReactionMs(): number {
    return this.reactionSamples.length ? Math.min(...this.reactionSamples) : 0;
  }

  /** Feeds the adaptive-difficulty controller. -1..1. */
  differential(): number {
    const p = this.exchanges.reduce((s, e) => s + e.playerQuality, 0);
    const a = this.exchanges.reduce((s, e) => s + e.aiQuality, 0);
    const t = p + a;
    return t > 0 ? clamp((p - a) / t, -1, 1) : 0;
  }

  resetAll(): void {
    for (const d of DEF) this.est[d.id].reset();
    this.punchNumbers.fill(0);
    this.targets.head = 0;
    this.targets.body = 0;
    this.intervals.clear();
    this.lastPunchT = 0;
  }
}

export const CHANNEL_DEFS = DEF;
export { expSmooth };
